import { ActivityCategoryInfo } from '../../interface';

export function participantAge(birthDate: string, today = new Date()): number | null {
    if (!birthDate) {
        return null;
    }
    const birth = new Date(birthDate);
    if (Number.isNaN(birth.getTime())) {
        return null;
    }
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
        age -= 1;
    }
    return age;
}

export function generateParticipantDisplayName(firstName: string, lastName: string): string {
    if (!firstName || !lastName) {
        return '';
    }
    return `${firstName.charAt(0).toUpperCase()}. ${lastName.substring(0, 9).toUpperCase()}`;
}

export function isMaleCategoryName(categoryName: string): boolean {
    return /\bMALE\b/i.test(categoryName) && !/\bFEMALE\b/i.test(categoryName);
}

export function isFemaleCategoryName(categoryName: string): boolean {
    return /\bFEMALE\b/i.test(categoryName);
}

export function categoryRequirementMismatches(
    birthDate: string,
    gender: string,
    categories: Array<Pick<ActivityCategoryInfo, 'id' | 'name'>>,
    categoryIds: number[],
    today = new Date(),
): string[] {
    const age = participantAge(birthDate, today);
    const invalidCategories: string[] = [];

    categoryIds.forEach((categoryId) => {
        const category = categories.find((item) => String(item.id) === String(categoryId));
        if (!category) {
            return;
        }
        const categoryName = category.name.toUpperCase();

        if (categoryName.includes('U-14') && (age === null || age < 13 || age > 14)) {
            invalidCategories.push(categoryName);
        } else if (categoryName.includes('U-35') && (age === null || age < 15 || age > 35)) {
            invalidCategories.push(categoryName);
        } else if (categoryName.includes('OVER 35') && (age === null || age < 36)) {
            invalidCategories.push(categoryName);
        }

        if (isMaleCategoryName(categoryName) && gender !== 'M') {
            invalidCategories.push(categoryName);
        } else if (isFemaleCategoryName(categoryName) && gender !== 'F') {
            invalidCategories.push(categoryName);
        }
    });

    return invalidCategories;
}

export function categoryHasDerivedState(
    category: Pick<ActivityCategoryInfo, 'is_setup_done' | 'is_bracket_done' | 'is_schedule_done' | 'participant_count'>,
): boolean {
    return Boolean(
        category.is_setup_done ||
        category.is_bracket_done ||
        category.is_schedule_done ||
        (category.participant_count || 0) > 0,
    );
}

export function changedCategoriesWithDerivedState(
    categories: ActivityCategoryInfo[],
    oldIds: number[],
    newIds: number[],
): Array<{ category: ActivityCategoryInfo; removed: boolean }> {
    const removedIds = oldIds.filter((id) => !newIds.includes(id));
    const addedIds = newIds.filter((id) => !oldIds.includes(id));
    return [...removedIds, ...addedIds]
        .map((id) => ({
            category: categories.find((item) => String(item.id) === String(id)),
            removed: removedIds.includes(id),
        }))
        .filter((row): row is { category: ActivityCategoryInfo; removed: boolean } =>
            Boolean(row.category && categoryHasDerivedState(row.category)),
        );
}
